import { useMemo } from 'react';

import { Box, Container, Flex, Headings, Text, useIsDeviceSize } from '@foradevelopment/fora-ui';
import {
  PayrollReportHistoryRowStatusEnum,
  PayrollReportHistoryRowViewModel,
  ReportingReceiptViewModel,
} from '@fora-monorepo/employer-my-pages-common/types';
import { getReadableTimestamp } from '@fora-monorepo/date-utils';
import { FORA_SOURCE } from '@fora-monorepo/fora-common';

import { ReportModalAndButton } from '../ReportModalAndButton';
import { useTypedTranslation } from '../../../i18n/i18n';
import { DownloadButtonDeviation, DownloadButtonInformation } from '../../../components';
import { DownloadButtonReportDetails } from '../../../components/DownloadButtons/DownloadButtonReportDetails';

type ReportHistoryDetailProps = PayrollReportHistoryRowViewModel & {
  reportIndex: number;
  getReportSummaryModalData: () => Promise<ReportingReceiptViewModel>;
};

export const ReportHistoryDetail = ({
  createdDate,
  adminReporterFullName,
  employerName,
  organizationNumber,
  source,
  hasEmploymentDeviations,
  hasEmploymentInformation,
  numberOfFiles,
  payrollReportId: reportId,
  reportIndex,
  payMonths,
  status,
  partnerNumberOrganization,
  getReportSummaryModalData,
}: ReportHistoryDetailProps) => {
  const { t } = useTypedTranslation();
  const { isMobile, isDesktop, isTablet } = useIsDeviceSize();

  const downloadButtonProps = useMemo(
    () => ({ reportId, partnerNumberOrganization, organizationNumber, employerName, createdDate }),
    [createdDate, employerName, organizationNumber, partnerNumberOrganization, reportId]
  );

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      paddingBottom={isDesktop ? 80 : 60}
      paddingLeft={isTablet ? 2 : 0}
      paddingRight={isTablet ? 2 : 0}
      flexDirection="column"
    >
      <Container maxWidth={578} width="100%" paddingLeft={20} paddingTop={50} paddingRight={20}>
        <Headings.H3 marginBottom={20}>
          {t('list.historyLog.adminReportedPayrollText', {
            adminReportedFullName: adminReporterFullName,
            reportSourcePayroll:
              source === FORA_SOURCE
                ? t('section.reportDetails.reportMethodManualInline')
                : t('section.reportDetails.reportMethodFileInline'),
          })}
        </Headings.H3>

        <Flex
          columnGap={40}
          rowGap={40}
          marginBottom={40}
          flexDirection={isMobile ? 'column' : 'row'}
          width="100%"
          flexWrap="wrap"
        >
          <Flex columnGap={40}>
            <Box minWidth="3.75rem">
              <Text color="black70">{t('table.reportLog.columnDate-1')} </Text>
              <Text>{getReadableTimestamp(createdDate)}</Text>
            </Box>
            <Box minWidth="3.75rem">
              <Text color="black70"> {t('table.reportLog.columnCompany-1')}</Text>
              <Text>{employerName}</Text>
            </Box>
          </Flex>

          <Box minWidth="3.75rem">
            <Text color="black70">{t('table.reportLog.columnReceipt-1')} </Text>
            <ReportModalAndButton reportIndex={reportIndex} getReportSummaryModalData={getReportSummaryModalData} />
          </Box>
        </Flex>
      </Container>
      {status !== PayrollReportHistoryRowStatusEnum.Processing && numberOfFiles > 0 && (
        <Container
          backgroundColor="black05"
          width={isDesktop ? 658 : '100%'}
          paddingLeft={isMobile ? 20 : 40}
          paddingRight={isMobile ? 20 : 40}
          paddingTop={20}
          paddingBottom={20}
        >
          <Headings.H5 marginBottom={10}>{t('list.historyLog.relatedFiles')} </Headings.H5>
          <Flex flexDirection="column" alignItems="flex-start">
            {!!payMonths.length && <DownloadButtonReportDetails {...downloadButtonProps} />}
            {(hasEmploymentInformation || payMonths.some(x => x.hasInformation)) && (
              <DownloadButtonInformation {...downloadButtonProps} />
            )}
            {(hasEmploymentDeviations || payMonths.some(x => x.hasDeviations)) && (
              <DownloadButtonDeviation {...downloadButtonProps} />
            )}
          </Flex>
        </Container>
      )}
    </Flex>
  );
};
